/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isMobile } from '@/utils/utils';
import { leaveCardView } from '@/store/slices/room';
import { fetchPersonalMemory, fetchRelationshipMemory } from '@/api/memory';
import {
  PersonalMemoryResponse,
  RelationshipMemoryResponse,
  EntityType,
  MEMORY_GROUP_LABELS,
  MEMORY_STATUS_LABELS,
  ENTITY_TYPE_LABELS,
  RELATION_TYPE_LABELS,
  TIP_TYPE_LABELS,
  MemoryItem,
} from '@/types/memory';
import style from './index.module.less';

type ViewState = 'idle' | 'loading' | 'ready' | 'empty' | 'error';
type TabType = 'personal' | 'relationship';

function LongTermMemory() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<TabType>('personal');
  const [viewState, setViewState] = useState<ViewState>('idle');

  // 个人记忆数据
  const [personalData, setPersonalData] = useState<PersonalMemoryResponse | null>(null);

  // 关系记忆数据
  const [relationshipData, setRelationshipData] = useState<RelationshipMemoryResponse | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState<EntityType>('person');

  const loadPersonalMemory = useCallback(async () => {
    setViewState('loading');
    try {
      const conversationId = 'demo-conversation-001';
      const data = await fetchPersonalMemory(conversationId);

      const hasItems = data.groups.some((group) => group.items.length > 0);
      if (!hasItems) {
        setViewState('empty');
        return;
      }

      setPersonalData(data);
      setViewState('ready');
    } catch (error) {
      console.error('Failed to load personal memory:', error);
      setViewState('error');
    }
  }, []);

  const loadRelationshipMemory = useCallback(async () => {
    setViewState('loading');
    try {
      const conversationId = 'demo-conversation-001';
      const data = await fetchRelationshipMemory(conversationId);

      if (!data.entities || data.entities.length === 0) {
        setViewState('empty');
        return;
      }

      setRelationshipData(data);

      // 默认选中 last_seen_at 最新的实体
      const sortedEntities = [...data.entities].sort(
        (a, b) => new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime()
      );
      setSelectedEntityId(sortedEntities[0]?.entity_id || null);

      setViewState('ready');
    } catch (error) {
      console.error('Failed to load relationship memory:', error);
      setViewState('error');
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'personal') {
      loadPersonalMemory();
    } else {
      loadRelationshipMemory();
    }
  }, [activeTab, loadPersonalMemory, loadRelationshipMemory]);

  const handleBack = () => {
    dispatch(leaveCardView());
  };

  const handleRetry = () => {
    if (activeTab === 'personal') {
      loadPersonalMemory();
    } else {
      loadRelationshipMemory();
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'string') {
      return value.length > 80 ? `${value.substring(0, 80)}...` : value;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value).substring(0, 80);
    }
    return String(value);
  };

  // 过滤实体
  const filteredEntities = useMemo(() => {
    if (!relationshipData) return [];

    const entities = relationshipData.entities.filter(
      (entity) => entity.entity_type === entityTypeFilter
    );

    return entities.sort(
      (a, b) => new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime()
    );
  }, [relationshipData, entityTypeFilter]);

  // 获取选中实体的相关数据
  const selectedEntity = useMemo(() => {
    if (!selectedEntityId || !relationshipData) return null;
    return relationshipData.entities.find((e) => e.entity_id === selectedEntityId);
  }, [selectedEntityId, relationshipData]);

  const selectedEdge = useMemo(() => {
    if (!selectedEntityId || !relationshipData) return null;
    return relationshipData.edges.find((e) => e.to_entity_id === selectedEntityId);
  }, [selectedEntityId, relationshipData]);

  const selectedFacts = useMemo(() => {
    if (!selectedEntityId || !relationshipData) return [];
    return relationshipData.facts
      .filter((f) => f.entity_id === selectedEntityId)
      .sort((a, b) => {
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;
        return b.confidence - a.confidence;
      });
  }, [selectedEntityId, relationshipData]);

  const selectedTips = useMemo(() => {
    if (!selectedEntityId || !relationshipData) return [];
    const tipData = relationshipData.interaction_tips.find((t) => t.entity_id === selectedEntityId);
    return tipData?.tips || [];
  }, [selectedEntityId, relationshipData]);

  // Loading State
  if (viewState === 'loading') {
    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>
            ‹ 返回
          </button>
          <h2 className={style.title}>长期记忆</h2>
        </div>
        <div className={style.content}>
          <div className={style.loadingState}>
            <div className={style.spinner} />
            <p>加载中...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (viewState === 'error') {
    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>
            ‹ 返回
          </button>
          <h2 className={style.title}>长期记忆</h2>
        </div>
        <div className={style.content}>
          <div className={style.errorState}>
            <div className={style.errorIcon}>⚠️</div>
            <h3>加载失败</h3>
            <p>无法获取记忆数据，请稍后重试</p>
            <button className={style.retryButton} onClick={handleRetry}>
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty State
  if (viewState === 'empty') {
    const emptyMessage =
      activeTab === 'personal'
        ? '目前还没有形成稳定的个人记忆。继续对话后，我们会把有用的信息整理在这里，你也可以手动添加。'
        : '还没有识别出可管理的关系实体（人/组织/项目）。当对话中出现稳定提及对象时，会自动生成。';

    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>
            ‹ 返回
          </button>
          <h2 className={style.title}>长期记忆</h2>
        </div>
        <div className={style.content}>
          <div className={style.tabs}>
            <button
              className={`${style.tab} ${activeTab === 'personal' ? style.active : ''}`}
              onClick={() => setActiveTab('personal')}
            >
              个人记忆
            </button>
            <button
              className={`${style.tab} ${activeTab === 'relationship' ? style.active : ''}`}
              onClick={() => setActiveTab('relationship')}
            >
              关系记忆
            </button>
          </div>
          <div className={style.emptyState}>
            <div className={style.emptyIcon}>📝</div>
            <h3>暂无数据</h3>
            <p>{emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // Ready State - 主要内容
  return (
    <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
      {/* Header */}
      <div className={style.header}>
        <button className={style.backButton} onClick={handleBack}>
          ‹ 返回
        </button>
        <h2 className={style.title}>长期记忆</h2>
      </div>

      {/* Content */}
      <div className={style.content}>
        {/* Tabs */}
        <div className={style.tabs}>
          <button
            className={`${style.tab} ${activeTab === 'personal' ? style.active : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            个人记忆
          </button>
          <button
            className={`${style.tab} ${activeTab === 'relationship' ? style.active : ''}`}
            onClick={() => setActiveTab('relationship')}
          >
            关系记忆
          </button>
        </div>

        {/* Personal Memory Tab */}
        {activeTab === 'personal' && personalData && (
          <div className={style.personalMemoryTab}>
            {personalData.groups.map((group) => {
              if (group.items.length === 0) return null;

              return (
                <div key={group.group} className={style.memoryGroup}>
                  <h3 className={style.groupTitle}>{MEMORY_GROUP_LABELS[group.group]}</h3>
                  <div className={style.memoryItems}>
                    {group.items.map((item) => (
                      <MemoryItemRow
                        key={item.memory_id}
                        item={item}
                        formatValue={formatValue}
                        formatTimestamp={formatTimestamp}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Relationship Memory Tab */}
        {activeTab === 'relationship' && relationshipData && (
          <div className={style.relationshipMemoryTab}>
            {/* 左侧：实体面板 */}
            <div className={style.entitiesPanel}>
              <div className={style.entityTypeTabs}>
                <button
                  className={`${style.entityTypeTab} ${
                    entityTypeFilter === 'person' ? style.active : ''
                  }`}
                  onClick={() => setEntityTypeFilter('person')}
                >
                  People
                </button>
                <button
                  className={`${style.entityTypeTab} ${
                    entityTypeFilter === 'org' ? style.active : ''
                  }`}
                  onClick={() => setEntityTypeFilter('org')}
                >
                  Orgs
                </button>
                <button
                  className={`${style.entityTypeTab} ${
                    entityTypeFilter === 'project' ? style.active : ''
                  }`}
                  onClick={() => setEntityTypeFilter('project')}
                >
                  Projects
                </button>
              </div>

              <div className={style.entitiesList}>
                {filteredEntities.map((entity) => {
                  const edge = relationshipData.edges.find(
                    (e) => e.to_entity_id === entity.entity_id
                  );
                  return (
                    <div
                      key={entity.entity_id}
                      className={`${style.entityItem} ${
                        selectedEntityId === entity.entity_id ? style.selected : ''
                      }`}
                      onClick={() => setSelectedEntityId(entity.entity_id)}
                    >
                      <div className={style.entityHeader}>
                        <span className={style.entityName}>{entity.display_name}</span>
                        <span className={style.entityType}>
                          {ENTITY_TYPE_LABELS[entity.entity_type]}
                        </span>
                      </div>
                      {edge && (
                        <>
                          <div className={style.entityRelation}>
                            {RELATION_TYPE_LABELS[edge.relation_type]}
                          </div>
                          <div className={style.strengthBar}>
                            <div
                              className={style.strengthFill}
                              style={{ width: `${edge.strength * 100}%` }}
                            />
                          </div>
                          {edge.notes && (
                            <div className={style.entityNotes}>
                              {edge.notes.substring(0, 50)}...
                            </div>
                          )}
                        </>
                      )}
                      <div className={style.entityMeta}>{formatTimestamp(entity.last_seen_at)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 中间：关系摘要 */}
            {selectedEntity && (
              <div className={style.relationshipSummary}>
                <h3 className={style.summaryTitle}>{selectedEntity.display_name}</h3>

                {selectedEntity.aliases && selectedEntity.aliases.length > 0 && (
                  <div className={style.aliases}>别名: {selectedEntity.aliases.join(', ')}</div>
                )}

                {selectedEdge && (
                  <div className={style.edgeInfo}>
                    <div className={style.infoRow}>
                      <span className={style.label}>关系类型:</span>
                      <span className={style.value}>
                        {RELATION_TYPE_LABELS[selectedEdge.relation_type]}
                      </span>
                    </div>
                    <div className={style.infoRow}>
                      <span className={style.label}>关系强度:</span>
                      <span className={style.value}>
                        {(selectedEdge.strength * 100).toFixed(0)}%
                      </span>
                    </div>
                    {selectedEdge.since && (
                      <div className={style.infoRow}>
                        <span className={style.label}>建立时间:</span>
                        <span className={style.value}>{selectedEdge.since}</span>
                      </div>
                    )}
                    {selectedEdge.last_interaction_at && (
                      <div className={style.infoRow}>
                        <span className={style.label}>最近互动:</span>
                        <span className={style.value}>
                          {formatTimestamp(selectedEdge.last_interaction_at)}
                        </span>
                      </div>
                    )}
                    {selectedEdge.notes && (
                      <div className={style.notes}>
                        <div className={style.label}>备注:</div>
                        <p>{selectedEdge.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className={style.keyFacts}>
                  <h4 className={style.sectionTitle}>Key Facts</h4>
                  {selectedFacts.slice(0, 8).map((fact) => (
                    <div key={fact.fact_id} className={style.factItem}>
                      <div className={style.factHeader}>
                        <span className={style.factCategory}>{fact.category}</span>
                        <span className={style.factConfidence}>
                          {(fact.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <p className={style.factContent}>{fact.content}</p>
                    </div>
                  ))}
                </div>

                <div className={style.interactionTips}>
                  <h4 className={style.sectionTitle}>Interaction Tips</h4>
                  {selectedTips.length > 0 ? (
                    selectedTips.slice(0, 3).map((tip, idx) => (
                      <div key={idx} className={style.tipItem}>
                        <span className={style.tipType}>{TIP_TYPE_LABELS[tip.type]}</span>
                        <p className={style.tipText}>{tip.text}</p>
                      </div>
                    ))
                  ) : (
                    <p className={style.noTips}>暂无互动建议</p>
                  )}
                </div>
              </div>
            )}

            {/* 右侧：证据面板 */}
            {selectedEntity && (
              <div className={style.evidencePanel}>
                <h3 className={style.panelTitle}>Evidence</h3>
                <div className={style.factsList}>
                  {selectedFacts.map((fact) => (
                    <div key={fact.fact_id} className={style.evidenceItem}>
                      <div className={style.evidenceHeader}>
                        <span className={style.evidenceCategory}>{fact.category}</span>
                        <span className={`${style.statusBadge} ${style[fact.status]}`}>
                          {MEMORY_STATUS_LABELS[fact.status]}
                        </span>
                      </div>
                      <p className={style.evidenceContent}>{fact.content}</p>
                      <div className={style.evidenceMeta}>
                        <span>置信度: {(fact.confidence * 100).toFixed(0)}%</span>
                        {fact.expires_at && <span>过期: {formatTimestamp(fact.expires_at)}</span>}
                      </div>
                      <div className={style.evidenceSource}>
                        <div className={style.sourceSnippet}>
                          {fact.source.redacted ? '内容已脱敏' : fact.source.snippet}
                        </div>
                        <button className={style.sourceButton}>
                          查看来源 (Turn {fact.source.turn_id})
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Memory Item Row Component
interface MemoryItemRowProps {
  item: MemoryItem;
  formatValue: (value: any) => string;
  formatTimestamp: (timestamp: string) => string;
}

function MemoryItemRow({ item, formatValue, formatTimestamp }: MemoryItemRowProps) {
  return (
    <div className={style.memoryItem}>
      <div className={style.itemHeader}>
        <span className={style.itemTitle}>{item.title}</span>
        <span className={`${style.statusBadge} ${style[item.status]}`}>
          {MEMORY_STATUS_LABELS[item.status]}
        </span>
      </div>
      <div className={style.itemValue}>{formatValue(item.value)}</div>
      <div className={style.itemMeta}>
        <span>置信度: {(item.confidence * 100).toFixed(0)}%</span>
        <span>更新: {formatTimestamp(item.updated_at)}</span>
      </div>
      {item.source && (
        <div className={style.itemSource}>
          来源: {item.source.redacted ? '内容已脱敏' : item.source.snippet.substring(0, 40)}...
        </div>
      )}
    </div>
  );
}

export default LongTermMemory;
